/**
 * @providesModule MatchUtils
 */

import ClientPropertyConstants from 'ClientPropertyConstants';

type MatchResponse = Match[];

type Match = {
  cash_fee: number;
  z_fee: number;
};

export type MatchSectionType = 'Z' | 'CASH' | 'SYNC' | 'LIVE' | 'BRACKET' | 'FRIENDS';

export type MatchSection = {
  type: MatchSectionType;
};

/** Function that returns true for match sections that should be displayed */
export type MatchSectionFilterFunction = (section: MatchSection) => boolean;

/** Default filter function that returns true for all sections */
export const INCLUDE_ALL_SECTIONS: MatchSectionFilterFunction = () => true;

/**
 * Enum for new user match filters
 * @enum {string} NewUserMatchFilter
 * @property {string} NONE - No filter applied
 * @property {string} EXPAND_Z - User played less than 5 Z games and no pro game
 * @property {string} EXPAND_PRO - User has played less than 10 games
 * @property {string} ALL_PRO_ENTRY_FEES - User played at least 3 pro games
 * @property {string} SPECIAL_MODES - User played 10 or more but less than 15 games
 */
export enum NewUserMatchFilter {
  NONE = 'NONE',
  EXPAND_Z = 'EXPAND_Z',
  EXPAND_PRO = 'EXPAND_PRO',
  SPECIAL_MODES = 'SPECIAL_MODES',
  ALL_PRO_ENTRY_FEES = 'ALL_PRO_ENTRY_FEES',
}

/**
 * Returns a new user's match filter value depending on the number and type of
 * matches they've played so far
 */
export const getNewUserMatchFilter = (
  matches: MatchResponse,
  totalGamesPlayed: number
): NewUserMatchFilter => {
  // 🎯 CRITICAL: Don't apply old FTUE match filtering when new FTUE V2 is enabled
  if (ClientPropertyConstants.getFTUEV2FeatureEnabled()) {
    return NewUserMatchFilter.NONE;
  }

  if (!ClientPropertyConstants.getMatchTemplateFTUEEnabled()) return NewUserMatchFilter.NONE;

  const cashGameCount = matches.filter((match) => match.cash_fee).length;
  const zGameCount = matches.length - cashGameCount; // Assume all non-cash games are Z games

  if (zGameCount < 5 && cashGameCount < 1) return NewUserMatchFilter.EXPAND_Z;

  if (totalGamesPlayed < 10) {
    return cashGameCount < 3
      ? NewUserMatchFilter.EXPAND_PRO
      : NewUserMatchFilter.ALL_PRO_ENTRY_FEES;
  }

  if (totalGamesPlayed < 15) return NewUserMatchFilter.SPECIAL_MODES;

  return NewUserMatchFilter.NONE;
};
